// import components
import { Box } from '@mui/material';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import external libraries
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

// import types
import dayjs from 'dayjs';
import { Patient } from '../types';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';

interface PatientTreatmentsProps {
  patient: Patient;
  isLoading?: boolean;
}

const formatDate = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('DD-MM-YYYY');
};

const PatientTreatments = ({ patient, isLoading }: PatientTreatmentsProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'careReceiversHome' });

  const treatments = useMemo(
    () =>
      (patient.treatments || []).map((treatment, index) => ({
        ...treatment,
        id: `treatment-${index}`
      })),
    [patient.treatments]
  );

  const columns = useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      {
        headerName: t('treatments.programName'),
        field: 'programName',
        type: 'string',
        flex: 1.5,
        minWidth: 200
      },
      {
        headerName: t('treatments.groupName'),
        field: 'groupName',
        type: 'string',
        flex: 1,
        minWidth: 150
      },
      {
        headerName: t('treatments.departmentName'),
        field: 'departmentName',
        type: 'string',
        flex: 1,
        minWidth: 150
      },
      {
        headerName: t('treatments.startDate'),
        field: 'actualOrPlannedOrExpectedStartDate',
        type: 'date',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => formatDate(value)
      },
      {
        headerName: t('treatments.endDate'),
        field: 'actualOrPlannedOrExpectedEndDate',
        type: 'date',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => formatDate(value)
      }
    ],
    [t]
  );

  return (
    <Box p={3.5}>
      <TableCRUDLayout
        getRowId={(row) => row.id}
        rows={treatments}
        columns={columns}
        loading={isLoading}
        dynamicHeight={true}
        pageSize={25}
        editable={false}
        disableActions={true}
        hideFooter={treatments.length < 25}
        initialState={{
          sorting: {
            sortModel: [
              { field: 'actualOrPlannedOrExpectedStartDate', sort: 'desc' }
            ]
          }
        }}
      />
    </Box>
  );
};

export default PatientTreatments;
